/**
 * Adapter - CardLifeDepositGateway
 *
 * Envoie le versement au backend CardLife:
 *   POST /operation-versements/complet
 *
 * Le backend est censé renvoyer un PDF en Base64 (reçu).
 */

import { IDepositGateway, DepositGatewayResult } from '../../application/ports/output/IDepositGateway';
import { DepositRequestDTO } from '../../application/dto/DepositRequestDTO';
import { buildBearerAuthHeader } from '../http/auth';
import { messageErreurApi } from '../http/erreurs';

const ENV = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env ?? {};
const CARDLIFE_BASE_URL = (ENV.VITE_CARDLIFE_BASE_URL ?? '').replace(/\/+$/g, '');
const DEPOSIT_ENDPOINT =
  ENV.VITE_CARDLIFE_DEPOSIT_ENDPOINT ?? '/api/v1/biotransac/operation-versements/complet';

function mapDenominationToBilletage(denomination: Record<number, number>) {
  const get = (value: number) => {
    const v = denomination[value];
    return typeof v === 'number' && Number.isFinite(v) ? v : 0;
  };

  // Mapping selon le DTO billetage fourni
  const billetage = {
    id: null as number | null,
    dixmille: get(10000),
    cinqmille: get(5000),
    deuxmille: get(2000),
    mille: get(1000),
    cinqcent: get(500),
    deuxcentcinquante: get(250),
    deuxcent: get(200),
    cent: get(100),
    cinquante: get(50),
    vingtcinq: get(25),
    dix: get(10),
    cinq: get(5),
    un: get(1),
    reference: '',
    nombreTotal: 0,
    montantTotal: 0,
    dateComptage: new Date().toISOString(),
    operationVersementId: null as number | null,
    operationRetraitId: null as number | null,
  };

  billetage.nombreTotal =
    billetage.dixmille +
    billetage.cinqmille +
    billetage.deuxmille +
    billetage.mille +
    billetage.cinqcent +
    billetage.deuxcentcinquante +
    billetage.deuxcent +
    billetage.cent +
    billetage.cinquante +
    billetage.vingtcinq +
    billetage.dix +
    billetage.cinq +
    billetage.un;
  billetage.montantTotal =
    billetage.dixmille * 10000 +
    billetage.cinqmille * 5000 +
    billetage.deuxmille * 2000 +
    billetage.mille * 1000 +
    billetage.cinqcent * 500 +
    billetage.deuxcentcinquante * 250 +
    billetage.deuxcent * 200 +
    billetage.cent * 100 +
    billetage.cinquante * 50 +
    billetage.vingtcinq * 25 +
    billetage.dix * 10 +
    billetage.cinq * 5 +
    billetage.un * 1;

  return billetage;
}

function findPdfBase64(value: any): string | undefined {
  if (!value) return undefined;
  if (typeof value === 'string') {
    // heuristique simple: une base64 PDF est généralement longue
    return value.length > 200 ? value : undefined;
  }
  if (typeof value !== 'object') return undefined;

  const direct =
    value.pdfBase64 ??
    value.pdf_base64 ??
    value.pdf ??
    value.receiptPdfBase64 ??
    value.recuBase64 ??
    value.recu ??
    value.documentBase64 ??
    value.document;

  if (typeof direct === 'string') return direct;

  // Parcours léger (1 niveau) pour éviter d'exploser
  for (const key of Object.keys(value)) {
    const nested = (value as any)[key];
    if (nested && typeof nested === 'object') {
      const hit = findPdfBase64(nested);
      if (hit) return hit;
    }
  }

  return undefined;
}

export class CardLifeDepositGateway implements IDepositGateway {
  async submitDeposit(input: DepositRequestDTO & { amount: number }): Promise<DepositGatewayResult> {
    const authHeader = buildBearerAuthHeader();
    const url = `${CARDLIFE_BASE_URL}${DEPOSIT_ENDPOINT}`;

    const billetage = mapDenominationToBilletage(input.denomination);

    // Extraire les infos du déposant tiers
    const isThirdParty = input.depositorInfo?.isThirdParty ?? false;
    const thirdParty = input.depositorInfo?.thirdParty;

    // Payload attendu: { operation, billetage, intitule, codeuser, estTiers, deposant* }
    const payload: Record<string, unknown> = {
      operation: {
        id: null,
        reference: '',
        codeOpe: '01210',           // Code opération versement (table code_operation)
        montant: input.amount,
        devise: 'XOF',
        sens: 'CREDIT',
        dateOperation: new Date().toISOString(),
        dateEnregistrement: new Date().toISOString(),
        response: '',
        message: '',
        numcpt: input.accountNumber,
        codeuser: input.cashierId,
        codeOperationId: 11,        // ID code_operation pour versement
      },
      billetage,
      intitule: input.notes ?? 'Versement espèces',
      codeuser: input.cashierId,
      // Champs déposant tiers
      estTiers: isThirdParty,
      deposantNom: isThirdParty ? (thirdParty?.name ?? '') : '',
      deposantPieceType: isThirdParty ? (thirdParty?.idType ?? '') : '',
      deposantPieceNumero: isThirdParty ? (thirdParty?.idNumber ?? '') : '',
      deposantContact: isThirdParty ? (thirdParty?.contact ?? '') : '',
    };

    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(authHeader ? { Authorization: authHeader } : {}),
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(messageErreurApi(response.status, text, "Le versement n'a pas pu être enregistré."));
    }

    const contentType = response.headers.get('content-type') ?? '';
    if (contentType.includes('application/json')) {
      const json = await response.json();
      const receiptPdfBase64 = findPdfBase64(json);
      const externalReference =
        (typeof json?.reference === 'string' && json.reference) ||
        (typeof json?.ref === 'string' && json.ref) ||
        (typeof json?.transactionRef === 'string' && json.transactionRef) ||
        undefined;

      const recu = json?.recu && typeof json.recu === 'object' ? json.recu : undefined;
      return { receiptPdfBase64, externalReference: recu?.reference ?? externalReference, recu };
    }

    // fallback: parfois le backend renvoie directement une string base64
    const text = await response.text();
    return { receiptPdfBase64: findPdfBase64(text) };
  }
}
